import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box, TextField, InputAdornment, MenuItem, Select, Chip, ButtonBase, Collapse, Tooltip, Button, Checkbox,
  IconButton,
} from '@mui/material';
import CallMergeRoundedIcon from '@mui/icons-material/CallMergeRounded';
import CallSplitRoundedIcon from '@mui/icons-material/CallSplitRounded';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FlipIcon from '@mui/icons-material/Flip';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LowPriorityRoundedIcon from '@mui/icons-material/LowPriorityRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded';
import KeyboardDoubleArrowUpRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowUpRounded';
import { GanttFamily, GanttFamilyJob, GanttSep } from './ganttTypes';
import { barStyle, ColourMap } from './barStyle';
import { useCustomerColours } from './useCustomerColours';
import { getSyncStatus, SyncStatus } from '../productionApi';
import { productionKeys } from '../queryKeys';
import {
  TOKENS, DOCK_HEIGHT, SHEETS_PER_MIN, DEFAULT_SETUP_MIN, DEFAULT_WASHUP_MIN,
  DRAG_THRESHOLD_PX, isPaperStock, fmtDims, hasCoating,
  InkFilter, inkKey, hasInk, MAX_CARD_SWATCHES,
} from './ganttConfig';

// A one-job "family" derived from a parent family, so a single job can ride the
// same drag / set-aside paths as a whole family. Durations are display-only;
// the backend computes the real ones from press standards when it schedules.
function singleJobFamily(fam: GanttFamily, job: GanttFamilyJob): GanttFamily {
  return {
    ...fam,
    famId: `${fam.famId}#${job.job}`,
    jobs: [job],
    setup: DEFAULT_SETUP_MIN,
    run: Math.max(1, Math.round(job.sheets / SHEETS_PER_MIN)),
    washup: DEFAULT_WASHUP_MIN,
    earliestDue: job.due,
  };
}

type SortKey = 'due' | 'cust' | 'forme' | 'count' | 'run';
type Tab = 'cartons' | 'paper' | 'elsewhere';

interface Props {
  families: GanttFamily[];
  totalJobs: number;
  elsewhereFamilies: GanttFamily[];
  elsewhereCount: number;
  // Active ink colour filter (click a card swatch to set/toggle) — cards and
  // scheduled bars not carrying the ink dim so shared-colour work stands out.
  inkFilter?: InkFilter | null;
  onInkToggle?: (sep: GanttSep) => void;
  onGrabFamily: (fam: GanttFamily, ev: React.PointerEvent) => void;
  // Click (no drag) on a family card → open the schedule menu anchored to it.
  onScheduleFamily: (fam: GanttFamily, anchor: HTMLElement) => void;
  onScheduleElsewhere: (fam: GanttFamily) => void;
  onReturnToBacklog: (fam: GanttFamily) => void;
  onSplitJobs: (jobNumbers: string[]) => void;
  onRejoinSplit: (splitKey: string) => void;
  draggingFamId?: string | null;
  // Expand the dock upward over the lanes (full height) / re-dock it. The
  // parent owns the state because the lanes are what give up the space.
  poolExpanded?: boolean;
  onTogglePool?: () => void;
}

const fmtDur = (min: number) => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h && m ? `${h}h ${m}m` : h ? `${h}h` : `${m}m`;
};
const fmtSheets = (n: number) => (n >= 1000 ? `${Math.round(n / 1000)}k` : String(n));
const dueText = (d: number | null) => (d == null ? '—' : d === 0 ? 'Today' : d < 0 ? `${-d}d late` : `d+${d}`);

// Coarse "x ago" for the Prism freshness chip — minutes/hours/days are plenty.
const relTime = (iso: string | null): string => {
  if (!iso) return 'never';
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

/**
 * SyncChip — shows when the Prism→production job sync last succeeded, sourced
 * from /production/sync-status (production.sync_state). Turns red when the sync
 * is overdue (`stale`) so operators know the Ready-to-Run data may be behind.
 */
function SyncChip({ sync }: { sync?: SyncStatus }) {
  if (!sync) return null;
  const stale = sync.stale;
  const label = sync.last_success_at ? `Prism · ${relTime(sync.last_success_at)}` : 'Prism · never synced';
  const tip = sync.last_success_at
    ? `Last Prism sync ${new Date(sync.last_success_at).toLocaleString()}${stale ? ' · overdue' : ''}`
    : 'No successful Prism sync recorded yet';
  return (
    <Tooltip title={tip} arrow>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.6, fontSize: 11, fontWeight: 600,
        color: stale ? TOKENS.bad : TOKENS.ink3, whiteSpace: 'nowrap', flexShrink: 0,
      }}>
        <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: stale ? TOKENS.bad : TOKENS.good }} />
        {label}
      </Box>
    </Tooltip>
  );
}
const dueTone = (d: number | null) => (d == null ? TOKENS.ink3 : d <= 0 ? TOKENS.bad : d <= 1 ? TOKENS.warn : TOKENS.ink3);

// The card's union ink strip: up to MAX_CARD_SWATCHES swatches (name on
// hover), the rest folded into "+n", plus a "read X of Y artworks" dot-dot-dot
// while the cache hasn't answered every artwork on the card. Clicking a swatch
// toggles the board-wide colour filter.
function CardSepStrip({ fam, inkFilter, onInkToggle }: {
  fam: GanttFamily; inkFilter?: InkFilter | null; onInkToggle?: (sep: GanttSep) => void;
}) {
  const total = fam.artworkCount ?? 0;
  const resolved = fam.resolvedArtworks ?? 0;
  const partial = total > 0 && resolved < total;
  if (fam.seps.length === 0 && !partial) return null;
  const visible = fam.seps.slice(0, MAX_CARD_SWATCHES);
  const hidden = fam.seps.slice(MAX_CARD_SWATCHES);
  return (
    <Box sx={{ display: 'flex', gap: '3px', flexShrink: 0, alignItems: 'center' }}>
      {visible.map((s, i) => {
        const active = !!inkFilter && inkKey(s) === inkFilter.key;
        return (
          <Tooltip key={i} title={onInkToggle ? `${s.name ?? s.short} — click to highlight this ink` : (s.name ?? s.short)} arrow>
            <Box
              onClick={onInkToggle ? (e) => { e.stopPropagation(); onInkToggle(s); } : undefined}
              onPointerDown={(e) => e.stopPropagation()}
              sx={{
                width: 9, height: 11, borderRadius: '2px', background: s.hex,
                border: s.role === 'coating' ? '1px dashed rgba(0,0,0,.4)' : '0.5px solid rgba(0,0,0,.18)',
                cursor: onInkToggle ? 'pointer' : 'default',
                outline: active ? `2px solid ${TOKENS.accent}` : 'none', outlineOffset: 1,
              }}
            />
          </Tooltip>
        );
      })}
      {hidden.length > 0 && (
        <Tooltip title={hidden.map((s) => s.name ?? s.short).join(' · ')} arrow>
          <Box sx={{ fontSize: 9.5, fontWeight: 700, color: TOKENS.ink3, px: '2px', whiteSpace: 'nowrap' }}>
            +{hidden.length}
          </Box>
        </Tooltip>
      )}
      {partial && (
        <Tooltip title={`Inks read for ${resolved} of ${total} artwork${total === 1 ? '' : 's'} on this card`} arrow>
          <Box sx={{ fontSize: 10, fontWeight: 800, color: TOKENS.ink3, lineHeight: 1, px: '1px' }}>…</Box>
        </Tooltip>
      )}
    </Box>
  );
}

function FamilyRow({ fam, colourMap, expanded, onToggle, onGrab, onClickCard, action, onGrabJob, onJobAside, onSplit, onRejoin, inkFilter, onInkToggle }: {
  fam: GanttFamily; colourMap: ColourMap; expanded: boolean; onToggle: () => void; onGrab: (ev: React.PointerEvent) => void;
  inkFilter?: InkFilter | null; onInkToggle?: (sep: GanttSep) => void;
  // Click the card (a press-and-release that never crossed the drag threshold)
  // → open the schedule menu. Mirrors the click-vs-drag split on Gantt bars.
  onClickCard: (anchor: HTMLElement) => void;
  action?: { icon: React.ReactNode; title: string; onClick: () => void };
  // When provided (Ready tab), expanded job rows can be peeled off individually:
  // dragged onto a press, or set aside, leaving the rest of the family grouped.
  onGrabJob?: (fam: GanttFamily, job: GanttFamilyJob, ev: React.PointerEvent) => void;
  onJobAside?: (fam: GanttFamily, job: GanttFamilyJob) => void;
  // Split a ticked subset into its own card; rejoin a split card to its group.
  onSplit?: (jobNumbers: string[]) => void;
  onRejoin?: () => void;
}) {
  const st = barStyle(fam.cust, fam.forme, colourMap);
  const sheets = fam.jobs.reduce((s, j) => s + j.sheets, 0);
  const jobCount = fam.jobs.length;
  // Sheet size + coating, so the group can be judged before it's dragged onto a
  // press — the coater requirement in particular changes which press will do.
  const dims = fmtDims(fam.length, fam.width);
  const coating = hasCoating(fam.coating) ? fam.coating : null;
  const [ticked, setTicked] = useState<Set<string>>(new Set());

  // Press-and-move on the card: distinguish a click (open schedule menu) from a
  // drag (move onto a press). We only hand off to onGrab once the pointer
  // travels past the threshold, so a plain click still opens the menu.
  const didDragRef = useRef(false);
  const onCardPointerDown = (ev: React.PointerEvent) => {
    if (ev.button !== 0) return;
    const sx = ev.clientX;
    const sy = ev.clientY;
    const start = ev;
    const move = (m: PointerEvent) => {
      if (Math.hypot(m.clientX - sx, m.clientY - sy) <= DRAG_THRESHOLD_PX) return;
      cleanup();
      didDragRef.current = true;
      onGrab(start);
    };
    const cleanup = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', cleanup);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', cleanup, { once: true });
  };

  const canSplit = !!onSplit && jobCount > 1;
  const toggleTick = (job: string) =>
    setTicked((prev) => { const n = new Set(prev); n.has(job) ? n.delete(job) : n.add(job); return n; });
  // A valid split needs a proper non-empty subset (leaves jobs behind).
  const splitReady = ticked.size >= 1 && ticked.size < jobCount;
  // Colour filter active and this card doesn't (known to) carry the ink → fade
  // it back so shared-colour work stands out. Dim, not hide: layout stays put
  // and "not known to match" (unread artwork) is honestly ambiguous.
  const inkDimmed = !!inkFilter && !hasInk(fam.seps, inkFilter.key);
  return (
    <Box sx={{
      border: `1px solid ${TOKENS.line}`, borderRadius: `${TOKENS.radiusChip}px`,
      background: TOKENS.panel, boxShadow: TOKENS.shadowSm, alignSelf: 'start', overflow: 'hidden',
      transition: 'box-shadow .12s, border-color .12s, opacity .15s',
      opacity: inkDimmed ? 0.35 : 1,
      '&:hover': { boxShadow: TOKENS.shadowMd, borderColor: `${TOKENS.accent}66`, opacity: 1 },
    }}>
      {/* grab + click surface: drag to a press, or click to open the schedule menu */}
      <Box
        onPointerDown={onCardPointerDown}
        onClick={(ev) => {
          if (didDragRef.current) { didDragRef.current = false; return; }
          onClickCard(ev.currentTarget);
        }}
        sx={{ display: 'flex', gap: 1.25, p: 1.75, cursor: 'pointer', userSelect: 'none' }}
      >
        <Box sx={{ width: 4, alignSelf: 'stretch', borderRadius: 99, flexShrink: 0, background: st.fill, border: st.border ? `1px solid ${st.border}` : 'none' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Box sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700, fontSize: 14, color: TOKENS.ink, whiteSpace: 'nowrap', flexShrink: 0 }}>{fam.forme ?? '—'}</Box>
            {/* A two-pass forme sits in the dock as TWO cards — one per pass —
                so the label is what tells them apart. Single-pass work (almost
                everything) shows nothing extra. */}
            {fam.twoPass && (
              <Box
                title={`Pass ${fam.pass ?? 1} — scheduled independently of the other pass`}
                sx={{
                  fontFamily: TOKENS.monoFont, fontSize: 10, fontWeight: 800, flexShrink: 0,
                  letterSpacing: '.3px', padding: '1px 4px', borderRadius: '3px',
                  color: TOKENS.ink2, border: `1px solid ${TOKENS.line}`,
                }}
              >
                PASS {fam.pass ?? 1}
              </Box>
            )}
            <Box sx={{ fontSize: 12, fontWeight: 600, color: TOKENS.ink2, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fam.cust}</Box>
            <Box sx={{ ml: 'auto', flexShrink: 0, fontSize: 11.5, fontWeight: 700, color: dueTone(fam.earliestDue), fontVariantNumeric: 'tabular-nums' }}>{dueText(fam.earliestDue)}</Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25 }}>
            <CardSepStrip fam={fam} inkFilter={inkFilter} onInkToggle={onInkToggle} />
            <Box sx={{ fontFamily: TOKENS.monoFont, fontSize: 11, color: TOKENS.ink3, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {jobCount} job{jobCount === 1 ? '' : 's'} · {fmtDur(fam.setup + fam.run + fam.washup)} · {fmtSheets(sheets)} sh
            </Box>
            <Box sx={{ ml: 'auto', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {onRejoin && (
                <Tooltip title="Rejoin to forme group" arrow>
                  <ButtonBase
                    onClick={(e) => { e.stopPropagation(); onRejoin(); }}
                    onPointerDown={(e) => e.stopPropagation()}
                    aria-label="rejoin to forme group"
                    sx={{
                      width: 22, height: 22, borderRadius: '6px', border: `1px solid ${TOKENS.line}`, color: TOKENS.ink3,
                      '&:hover': { borderColor: `${TOKENS.accent}66`, color: TOKENS.accent, background: `${TOKENS.accent}0a` },
                    }}
                  >
                    <CallMergeRoundedIcon sx={{ fontSize: 15 }} />
                  </ButtonBase>
                </Tooltip>
              )}
              {action && (
                <Tooltip title={action.title} arrow>
                  <ButtonBase
                    onClick={(e) => { e.stopPropagation(); action.onClick(); }}
                    onPointerDown={(e) => e.stopPropagation()}
                    aria-label={action.title}
                    sx={{
                      width: 22, height: 22, borderRadius: '6px', border: `1px solid ${TOKENS.line}`, color: TOKENS.ink3,
                      '&:hover': { borderColor: `${TOKENS.accent}66`, color: TOKENS.accent, background: `${TOKENS.accent}0a` },
                    }}
                  >
                    {action.icon}
                  </ButtonBase>
                </Tooltip>
              )}
              <ButtonBase
                onClick={(e) => { e.stopPropagation(); onToggle(); }}
                onPointerDown={(e) => e.stopPropagation()}
                aria-label="expand jobs"
                sx={{ width: 22, height: 22, borderRadius: '6px', border: `1px solid ${TOKENS.line}` }}
              >
                <ChevronRightIcon sx={{ fontSize: 16, color: TOKENS.ink3, transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }} />
              </ButtonBase>
            </Box>
          </Box>
          {(fam.stockCode || dims || coating) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.9 }}>
              {/* Raw-material code (production.jobs.stock_code) — the code, not the
                  description: it's what the planner matches against the paper on
                  the floor, and a family is one stock code by construction (it's
                  part of the grouping key), so it describes every job on the card. */}
              {fam.stockCode && (
                <Box sx={{
                  fontFamily: TOKENS.monoFont, fontSize: 10.5, fontWeight: 600, color: TOKENS.ink2,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0,
                }}>
                  {fam.stockCode}
                </Box>
              )}
              {dims && (
                <Box sx={{
                  fontFamily: TOKENS.monoFont, fontSize: 10.5, color: TOKENS.ink3,
                  fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', flexShrink: 0,
                }}>
                  {dims} mm
                </Box>
              )}
              {coating && (
                <Box sx={{
                  fontSize: 9.5, fontWeight: 700, letterSpacing: '.03em', color: TOKENS.ink2,
                  border: `1px solid ${TOKENS.line}`, borderRadius: '5px', px: 0.6, py: '1px',
                  whiteSpace: 'nowrap', flexShrink: 0,
                }}>
                  {coating}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 1, pb: 1, borderTop: `1px solid ${TOKENS.lineSoft}` }}>
          {fam.jobs.length > 1 && (onGrabJob || canSplit) && (
            <Box sx={{ fontSize: 10, color: TOKENS.ink3, px: 0.5, pt: 0.5 }}>
              {canSplit ? 'Tick jobs to split into a new group, or drag one to a press.' : 'Drag a single job to a press, or set it aside.'}
            </Box>
          )}
          {fam.jobs.map((j) => {
            const peelable = fam.jobs.length > 1 && !!onGrabJob;
            return (
              <Box
                key={j.job}
                onPointerDown={peelable ? (ev) => { ev.stopPropagation(); onGrabJob!(fam, j, ev); } : undefined}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, py: 0.4, borderRadius: '6px',
                  cursor: peelable ? 'grab' : 'default', userSelect: peelable ? 'none' : 'auto',
                  '&:hover': peelable ? { background: TOKENS.lineSoft } : undefined,
                  '&:hover .peel-aside': { opacity: 1 },
                }}
              >
                {canSplit && (
                  <Checkbox
                    size="small" checked={ticked.has(j.job)}
                    onChange={() => toggleTick(j.job)}
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    sx={{ p: 0, flexShrink: 0, '& .MuiSvgIcon-root': { fontSize: 16 } }}
                  />
                )}
                {peelable && <DragIndicatorRoundedIcon sx={{ fontSize: 14, color: TOKENS.ink3, flexShrink: 0, ml: -0.5 }} />}
                <Box sx={{ fontFamily: TOKENS.monoFont, fontWeight: 600, fontSize: 11, color: TOKENS.ink2, flexShrink: 0 }}>{j.job}</Box>
                <Box sx={{ flex: 1, minWidth: 0, fontSize: 11, color: TOKENS.ink2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{j.desc}</Box>
                <Box sx={{ fontFamily: TOKENS.monoFont, fontSize: 10.5, color: TOKENS.ink3, flexShrink: 0 }}>{fmtSheets(j.sheets)} sh</Box>
                <Box sx={{ fontSize: 10.5, color: dueTone(j.due), fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{dueText(j.due)}</Box>
                {peelable && onJobAside && (
                  <Tooltip title="Schedule this job elsewhere" arrow>
                    <ButtonBase
                      className="peel-aside"
                      onClick={(e) => { e.stopPropagation(); onJobAside(fam, j); }}
                      onPointerDown={(e) => e.stopPropagation()}
                      aria-label="schedule this job elsewhere"
                      sx={{ flexShrink: 0, width: 18, height: 18, borderRadius: '5px', color: TOKENS.ink3, opacity: 0, transition: 'opacity .12s', '&:hover': { color: TOKENS.accent } }}
                    >
                      <LowPriorityRoundedIcon sx={{ fontSize: 13 }} />
                    </ButtonBase>
                  </Tooltip>
                )}
              </Box>
            );
          })}
          {canSplit && ticked.size > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 0.75, mt: 0.25, borderTop: `1px solid ${TOKENS.lineSoft}` }}>
              <Box sx={{ fontSize: 11, color: TOKENS.ink3 }}>{ticked.size} ticked</Box>
              <Button
                size="small" variant="outlined" startIcon={<CallSplitRoundedIcon sx={{ fontSize: 15 }} />}
                disabled={!splitReady}
                onClick={() => { onSplit!(Array.from(ticked)); setTicked(new Set()); }}
                sx={{ ml: 'auto', textTransform: 'none', fontSize: 11.5, py: 0.25, borderRadius: '7px' }}
              >
                Split into new group
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function ReadyToRunDock({
  families, elsewhereFamilies, elsewhereCount,
  onGrabFamily, onScheduleFamily, onScheduleElsewhere, onReturnToBacklog, onSplitJobs, onRejoinSplit,
  inkFilter, onInkToggle, poolExpanded = false, onTogglePool,
}: Props) {
  // Counts pool expand/re-dock toggles so the card-area settle animation
  // re-arms each time (see the keyframe note on the card area below).
  const [poolToggles, setPoolToggles] = useState(0);
  const prevPoolState = useRef(poolExpanded);
  useEffect(() => {
    if (prevPoolState.current !== poolExpanded) {
      prevPoolState.current = poolExpanded;
      setPoolToggles((n) => n + 1);   // never on first mount — a fresh load doesn't animate
    }
  }, [poolExpanded]);
  const [tab, setTab] = useState<Tab>('cartons');
  const [q, setQ] = useState('');
  const [cust, setCust] = useState('all');
  const [stock, setStock] = useState('all');
  const [sort, setSort] = useState<SortKey>('due');
  const [twoPassOnly, setTwoPassOnly] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Prism freshness — refetched on the provider's 30s staleTime plus a 60s
  // poll so the "x ago" label keeps ticking even without other board activity.
  const { data: sync } = useQuery({
    queryKey: productionKeys.syncStatus(),
    queryFn: getSyncStatus,
    refetchInterval: 60_000,
  });
  const colourMap = useCustomerColours();

  // Split the backlog: "Paper" = raw paper/board stock (parade, etc.) that still
  // runs on-press, keyed off the stock_code prefix; everything else is "Cartons".
  const { cartonFamilies, paperFamilies } = useMemo(() => {
    const cartons: GanttFamily[] = [];
    const paper: GanttFamily[] = [];
    for (const f of families) (isPaperStock(f.stockCode) ? paper : cartons).push(f);
    return { cartonFamilies: cartons, paperFamilies: paper };
  }, [families]);
  const cartonJobs = useMemo(() => cartonFamilies.reduce((s, f) => s + f.jobs.length, 0), [cartonFamilies]);
  const paperJobs = useMemo(() => paperFamilies.reduce((s, f) => s + f.jobs.length, 0), [paperFamilies]);

  const showElsewhere = tab === 'elsewhere';
  const showPaper = tab === 'paper';
  // The backlog list the filters/sort operate on for the active tab (Elsewhere
  // has its own list and skips the filter bar entirely).
  const baseList = showPaper ? paperFamilies : cartonFamilies;

  const customers = useMemo(
    () => Array.from(new Set(baseList.map((f) => f.cust).filter(Boolean))).sort() as string[],
    [baseList],
  );
  // Raw-material codes present in the active tab. Cartons and Paper hold
  // disjoint stock codes (the tab split IS a stock-code prefix test), so the
  // list is rebuilt per tab and the selection is cleared on tab change —
  // otherwise a carried-over code would silently empty the other tab.
  const stockCodes = useMemo(
    () => Array.from(new Set(baseList.map((f) => f.stockCode).filter(Boolean))).sort() as string[],
    [baseList],
  );
  const selectTab = (t: Tab) => { setTab(t); setStock('all'); };
  // Cards, not jobs — a two-pass forme is TWO cards, so this counts the runs
  // still needing a slot, which is what the planner is actually placing.
  const twoPassCount = useMemo(() => baseList.filter((f) => f.twoPass).length, [baseList]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    let list = baseList.filter((f) => {
      if (cust !== 'all' && f.cust !== cust) return false;
      if (stock !== 'all' && f.stockCode !== stock) return false;
      if (twoPassOnly && !f.twoPass) return false;
      if (!ql) return true;
      if ((f.forme ?? '').toLowerCase().includes(ql)) return true;
      if ((f.cust ?? '').toLowerCase().includes(ql)) return true;
      if ((f.custName ?? '').toLowerCase().includes(ql)) return true;
      return f.jobs.some((j) => j.job.toLowerCase().includes(ql) || j.desc.toLowerCase().includes(ql));
    });
    const cmp: Record<SortKey, (a: GanttFamily, b: GanttFamily) => number> = {
      due: (a, b) => (a.earliestDue ?? 9999) - (b.earliestDue ?? 9999),
      cust: (a, b) => (a.cust ?? '').localeCompare(b.cust ?? ''),
      forme: (a, b) => (a.forme ?? '').localeCompare(b.forme ?? ''),
      count: (a, b) => b.jobs.length - a.jobs.length,
      run: (a, b) => (b.setup + b.run + b.washup) - (a.setup + a.run + a.washup),
    };
    return [...list].sort(cmp[sort]);
  }, [baseList, q, cust, stock, twoPassOnly, sort]);

  const filtering = q.trim() !== '' || twoPassOnly || cust !== 'all' || stock !== 'all';
  const matchJobs = filtered.reduce((s, f) => s + f.jobs.length, 0);
  // Live workload totals for whatever the filters currently match — so a planner
  // can see "how much" a customer / due-soon slice actually is. Sheets sum the
  // job run-lengths; hours sum each family's full press-time (setup+run+washup).
  const totalSheets = filtered.reduce((s, f) => s + f.jobs.reduce((t, j) => t + j.sheets, 0), 0);
  const totalHours = filtered.reduce((s, f) => s + f.setup + f.run + f.washup, 0) / 60;
  const clearFilters = () => { setQ(''); setCust('all'); setStock('all'); setSort('due'); setTwoPassOnly(false); };

  const toggleExpand = (famId: string) =>
    setExpanded((prev) => { const n = new Set(prev); n.has(famId) ? n.delete(famId) : n.add(famId); return n; });

  const rows = showElsewhere ? elsewhereFamilies : filtered;

  // Paper tab: break the (already filtered/sorted) families into sections by
  // stock_code so every job group on the same material sits under one header.
  const paperGroups = useMemo(() => {
    if (!showPaper) return [];
    const map = new Map<string, GanttFamily[]>();
    for (const f of filtered) {
      const key = f.stockCode ?? '—';
      const arr = map.get(key);
      if (arr) arr.push(f);
      else map.set(key, [f]);
    }
    return Array.from(map.entries())
      .map(([key, fams]) => ({
        key,
        materialDesc: fams.find((f) => f.materialDesc)?.materialDesc ?? null,
        families: fams,
        formes: fams.length,
        jobs: fams.reduce((s, f) => s + f.jobs.length, 0),
        sheets: fams.reduce((s, f) => s + f.jobs.reduce((t, j) => t + j.sheets, 0), 0),
        hours: fams.reduce((s, f) => s + f.setup + f.run + f.washup, 0) / 60,
      }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [showPaper, filtered]);

  // One card renderer shared by the flat (Cartons/Elsewhere) grid and the
  // per-stock-code sections on the Paper tab.
  const renderFamily = (fam: GanttFamily) => (
    <FamilyRow
      key={fam.famId} fam={fam} colourMap={colourMap} expanded={expanded.has(fam.famId)}
      inkFilter={inkFilter} onInkToggle={onInkToggle}
      onToggle={() => toggleExpand(fam.famId)}
      onGrab={(ev) => onGrabFamily(fam, ev)}
      onClickCard={(anchor) => onScheduleFamily(fam, anchor)}
      action={showElsewhere
        ? { icon: <UndoRoundedIcon sx={{ fontSize: 15 }} />, title: 'Return to backlog', onClick: () => onReturnToBacklog(fam) }
        : undefined}
      onGrabJob={showElsewhere ? undefined : (f, job, ev) => onGrabFamily(singleJobFamily(f, job), ev)}
      onJobAside={showElsewhere ? undefined : (f, job) => onScheduleElsewhere(singleJobFamily(f, job))}
      onSplit={showElsewhere ? undefined : (jobNumbers) => onSplitJobs(jobNumbers)}
      onRejoin={!showElsewhere && fam.splitKey ? () => onRejoinSplit(fam.splitKey!) : undefined}
    />
  );

  // Shared card-grid layout for a flat list or a single stock-code section.
  const gridSx = {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gridAutoRows: 'max-content', alignContent: 'start', alignItems: 'start', gap: 2,
  } as const;

  // One consistent height, radius, border and font size for every control in the
  // toolbar so the tabs, search, selects and chips read as a single family
  // (matching the board's thin-line, soft-ink look, not MUI's heavy defaults).
  const CONTROL_H = 34;
  const CONTROL_FONT = 13;
  // Symmetric vertical padding drives the field height while keeping the text
  // vertically centred — hard-setting height + py:0 squashed it to the top.
  const FIELD_PY = '7px';

  const tabSx = (active: boolean) => ({
    fontSize: CONTROL_FONT, fontWeight: 600, textTransform: 'none' as const, minWidth: 0, px: 1.5,
    height: CONTROL_H, borderRadius: `${TOKENS.radiusChip}px`, color: active ? TOKENS.ink : TOKENS.ink3,
    background: active ? `${TOKENS.accent}14` : 'transparent',
    '&:hover': { background: active ? `${TOKENS.accent}1f` : TOKENS.lineSoft },
  });

  // Shared outlined-field styling for the search box + selects.
  const fieldSx = {
    '& .MuiInputBase-root': {
      fontSize: CONTROL_FONT, color: TOKENS.ink,
      borderRadius: `${TOKENS.radiusChip}px`, background: TOKENS.panel,
    },
    '& .MuiInputBase-input': { paddingTop: FIELD_PY, paddingBottom: FIELD_PY },
    '& .MuiSelect-select': { paddingTop: FIELD_PY, paddingBottom: FIELD_PY, minHeight: 'unset' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.line },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.ink3 },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.accent, borderWidth: 1 },
  };

  // Shared chip styling — same height/radius/font as the fields; filled+primary
  // marks the active state (Abaris orange), outlined uses the soft line + ink.
  const chipSx = (active: boolean) => ({
    height: CONTROL_H, fontSize: CONTROL_FONT, borderRadius: `${TOKENS.radiusChip}px`, fontWeight: 500,
    ...(active ? {} : { borderColor: TOKENS.line, color: TOKENS.ink2 }),
    '& .MuiChip-icon': { color: active ? 'inherit' : TOKENS.ink3 },
  });

  return (
    <Box sx={{ flex: 1, minHeight: DOCK_HEIGHT, borderTop: `1px solid ${TOKENS.line}`, display: 'flex', flexDirection: 'column', background: TOKENS.bg }}>
      {/* toolbar: tabs on the left, filters (Ready tab only) on the right */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderBottom: `1px solid ${TOKENS.lineSoft}`, background: TOKENS.panel, flexShrink: 0, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Button onClick={() => selectTab('cartons')} sx={tabSx(tab === 'cartons')}>
            Cartons <Box component="span" sx={{ ml: 0.5, fontSize: 11, fontWeight: 400, color: TOKENS.ink3 }}>{cartonJobs}</Box>
          </Button>
          <Button
            onClick={() => selectTab('paper')} sx={tabSx(tab === 'paper')}
            disabled={paperJobs === 0 && tab !== 'paper'}
          >
            Paper <Box component="span" sx={{ ml: 0.5, fontSize: 11, fontWeight: 400, color: TOKENS.ink3 }}>{paperJobs}</Box>
          </Button>
          <Button
            onClick={() => selectTab('elsewhere')} sx={tabSx(tab === 'elsewhere')}
            disabled={elsewhereCount === 0 && tab !== 'elsewhere'}
          >
            Elsewhere <Box component="span" sx={{ ml: 0.5, fontSize: 11, fontWeight: 400, color: TOKENS.ink3 }}>{elsewhereCount}</Box>
          </Button>
        </Box>

        {/* Active ink colour filter — set by clicking a swatch on any card;
            everything not carrying the ink is dimmed (dock AND schedule bars).
            Lives outside the Ready-tab filter block because it applies to every
            tab and to the board above. */}
        {inkFilter && onInkToggle && (
          <Chip
            size="small"
            icon={<Box sx={{ width: 11, height: 11, borderRadius: '3px', background: inkFilter.hex, border: '0.5px solid rgba(0,0,0,.25)', ml: '6px !important' }} />}
            label={`Ink: ${inkFilter.name}`}
            title="Showing work that carries this ink — click × to clear"
            onDelete={() => onInkToggle({ short: inkFilter.name, name: inkFilter.name, hex: inkFilter.hex, role: 'spot' })}
            variant="filled" color="primary" sx={chipSx(true)}
          />
        )}

        {!showElsewhere && (
          <>
            <TextField
              size="small" placeholder="Search job, customer, forme…" value={q}
              onChange={(e) => setQ(e.target.value)}
              sx={{ ml: 0.5, width: 240, ...fieldSx }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: TOKENS.ink3 }} /></InputAdornment> }}
            />
            <Select size="small" value={cust} onChange={(e) => setCust(e.target.value)} sx={{ width: 160, ...fieldSx }}>
              <MenuItem value="all" sx={{ fontSize: CONTROL_FONT }}>All customers</MenuItem>
              {customers.map((c) => <MenuItem key={c} value={c} sx={{ fontSize: CONTROL_FONT }}>{c}</MenuItem>)}
            </Select>
            {/* Raw-material filter — same shape as the customer one. Mono, because
                a stock code is read character-by-character (RC610 vs RC660). */}
            <Select
              size="small" value={stock} onChange={(e) => setStock(e.target.value)}
              disabled={stockCodes.length === 0}
              sx={{ width: 210, ...fieldSx, '& .MuiSelect-select': { ...fieldSx['& .MuiSelect-select'], fontFamily: stock === 'all' ? 'inherit' : TOKENS.monoFont } }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
            >
              <MenuItem value="all" sx={{ fontSize: CONTROL_FONT }}>All materials</MenuItem>
              {stockCodes.map((s) => (
                <MenuItem key={s} value={s} sx={{ fontSize: CONTROL_FONT, fontFamily: TOKENS.monoFont }}>{s}</MenuItem>
              ))}
            </Select>
            <Select size="small" value={sort} onChange={(e) => setSort(e.target.value as SortKey)} sx={{ width: 170, ...fieldSx }}>
              <MenuItem value="due" sx={{ fontSize: CONTROL_FONT }}>Sort: Due date</MenuItem>
              <MenuItem value="cust" sx={{ fontSize: CONTROL_FONT }}>Sort: Customer</MenuItem>
              <MenuItem value="forme" sx={{ fontSize: CONTROL_FONT }}>Sort: Forme</MenuItem>
              <MenuItem value="count" sx={{ fontSize: CONTROL_FONT }}>Sort: Job count</MenuItem>
              <MenuItem value="run" sx={{ fontSize: CONTROL_FONT }}>Sort: Run time</MenuItem>
            </Select>
            {/* Two-pass work is a different planning problem — the fronts get
                batched and the backs come back later — so it needs isolating
                from the rest of the pool. Disabled rather than hidden when
                there are none: "no two-pass work today" is worth knowing, and
                a control that comes and goes is harder to trust than one that
                greys out. */}
            <Chip
              size="small" icon={<FlipIcon sx={{ fontSize: 14 }} />} label={`2-pass · ${twoPassCount}`}
              title="Show only jobs that print both sides — one card per pass"
              disabled={twoPassCount === 0 && !twoPassOnly}
              onClick={() => setTwoPassOnly((v) => !v)} variant={twoPassOnly ? 'filled' : 'outlined'}
              color={twoPassOnly ? 'primary' : 'default'} sx={chipSx(twoPassOnly)}
            />
            {filtering && (
              <Chip
                size="small" icon={<CloseRoundedIcon sx={{ fontSize: 14 }} />} label="Clear"
                onClick={clearFilters} variant="outlined" sx={chipSx(false)}
              />
            )}
            <Box sx={{ flex: 1 }} />
            <Box sx={{ fontSize: 10.5, color: TOKENS.ink3, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {filtering && <Box component="span">{filtered.length} formes · {matchJobs} jobs · </Box>}
              <Box component="span"><b style={{ color: TOKENS.ink2 }}>{fmtSheets(totalSheets)}</b> sheets · <b style={{ color: TOKENS.ink2 }}>{totalHours.toFixed(1)}h</b></Box>
            </Box>
          </>
        )}
        {showElsewhere && (
          <Box sx={{ ml: 1, fontSize: 11.5, color: TOKENS.ink3 }}>
            Jobs scheduled outside this system — drag onto a press or return to backlog.
          </Box>
        )}
        <Box sx={{ ml: 'auto', pl: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <SyncChip sync={sync} />
          {/* Expand the pool over the lanes / re-dock it. The chevron turns
              with the same easing the lanes collapse with, so the two read as
              one motion. Grabbing a card while expanded re-docks automatically
              (the lanes are the drop target), handled by the parent. */}
          {onTogglePool && (
            <Tooltip title={poolExpanded ? 'Re-dock the job pool' : 'Expand the job pool to full height'}>
              <IconButton size="small" onClick={onTogglePool}
                aria-label={poolExpanded ? 'Re-dock the job pool' : 'Expand the job pool'}
                sx={{
                  width: 26, height: 26, borderRadius: '7px',
                  color: poolExpanded ? TOKENS.accent : TOKENS.ink3,
                  border: `1px solid ${poolExpanded ? `${TOKENS.accent}66` : TOKENS.line}`,
                  bgcolor: poolExpanded ? `${TOKENS.accent}14` : 'transparent',
                  transition: 'color .2s, border-color .2s, background-color .2s, transform .15s',
                  '&:hover': { color: TOKENS.accent, borderColor: `${TOKENS.accent}66`, transform: 'translateY(-1px)' },
                  '&:active': { transform: 'scale(.9)' },
                }}>
                {/* The chevron flips with an overshoot, so the press feels like
                    it kicked something off rather than toggled a flag. */}
                <KeyboardDoubleArrowUpRoundedIcon sx={{
                  fontSize: 18,
                  transform: poolExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform .6s cubic-bezier(.34,1.56,.64,1)',
                  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* vertically-scrolling card area. When the pool opens up, the newly
          revealed cards fade in — opacity only, so the compositor does it and
          nothing re-lays-out. A one-shot keyframe, re-armed on every toggle by
          alternating two identical keyframe names (a changing animation-name
          restarts the animation without remounting the scroll area, so scroll
          position survives). */}
      <Box sx={{
        flex: 1, minHeight: 0, overflowY: 'auto', p: 2,
        '@keyframes dockSettleA': { from: { opacity: 0.35 }, to: { opacity: 1 } },
        '@keyframes dockSettleB': { from: { opacity: 0.35 }, to: { opacity: 1 } },
        animation: poolToggles === 0
          ? 'none'
          : `${poolToggles % 2 ? 'dockSettleA' : 'dockSettleB'} .6s ease-out both`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}>
        {showElsewhere && rows.length === 0 ? (
          <Box sx={{ fontSize: 12.5, color: TOKENS.ink3 }}>Nothing scheduled elsewhere.</Box>
        ) : showPaper ? (
          paperGroups.length === 0 ? (
            <Box sx={{ fontSize: 12.5, color: TOKENS.ink3 }}>No paper / raw-material jobs in the backlog.</Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {paperGroups.map((g) => (
                <Box key={g.key}>
                  {/* stock-code section header — all job groups on this material */}
                  <Box sx={{
                    display: 'flex', alignItems: 'baseline', gap: 1.25, mb: 1.25, pb: 0.75,
                    borderBottom: `2px solid ${TOKENS.line}`,
                  }}>
                    <Box sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700, fontSize: 14, color: TOKENS.ink, whiteSpace: 'nowrap', flexShrink: 0 }}>{g.key}</Box>
                    {g.materialDesc && (
                      <Box sx={{ fontSize: 12, fontWeight: 500, color: TOKENS.ink2, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.materialDesc}</Box>
                    )}
                    <Box sx={{ ml: 'auto', flexShrink: 0, fontSize: 11, color: TOKENS.ink3, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                      {g.formes} forme{g.formes === 1 ? '' : 's'} · {g.jobs} job{g.jobs === 1 ? '' : 's'} · {fmtSheets(g.sheets)} sh · {g.hours.toFixed(1)}h
                    </Box>
                  </Box>
                  <Box sx={gridSx}>
                    {g.families.map(renderFamily)}
                  </Box>
                </Box>
              ))}
            </Box>
          )
        ) : (
          <Box sx={gridSx}>
            {rows.map(renderFamily)}
          </Box>
        )}
      </Box>
    </Box>
  );
}
